import { useState, useCallback, Fragment, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
    ChartPieIcon,
    BanknotesIcon,
    CreditCardIcon,
    CircleStackIcon,
    ChatBubbleLeftIcon,
    AdjustmentsHorizontalIcon,
    SunIcon,
    MoonIcon,
    UserCircleIcon,
    UserIcon,
    ArrowsUpDownIcon,
    ArrowRightStartOnRectangleIcon,
    ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileDrawer } from './MobileDrawer';
import { LogoFull } from '@/components/LogoFull';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useLayout } from '@/contexts/LayoutContext';
import { InstallPromptBanner } from '@/components/InstallPromptBanner';

interface LayoutProps {
    children: ReactNode;
}

interface NavItem {
    label: string;
    path: string;
    icon: typeof ChartPieIcon;
    requiresAdmin?: boolean;
}

const mainNavItems: NavItem[] = [
    { label: 'Net worth', path: '/dashboard', icon: ChartPieIcon },
    { label: 'Assets', path: '/assets', icon: BanknotesIcon },
    { label: 'Debts', path: '/debts', icon: CreditCardIcon },
];

const bottomNavItems: NavItem[] = [
    { label: 'Credits', path: '/credits', icon: CircleStackIcon },
    { label: 'Feedback', path: '/feedback', icon: ChatBubbleLeftIcon },
    { label: 'Admin', path: '/admin', icon: ShieldCheckIcon, requiresAdmin: true },
];

export function Layout({ children }: LayoutProps) {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const stored = localStorage.getItem('sidebarCollapsed');
        return stored === 'true';
    });
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const { user, logout } = useAuth();
    const { effectiveTheme, setTheme } = useTheme();
    const { isFullWidth } = useLayout();

    const isAdmin = user?.roles?.includes('ROLE_ADMIN') ?? false;

    const filteredBottomNavItems = bottomNavItems.filter(
        (item) => !item.requiresAdmin || isAdmin
    );

    const isActive = (path: string) => location.pathname === path;

    const toggleTheme = () => {
        setTheme(effectiveTheme === 'dark' ? 'light' : 'dark');
    };

    const toggleSidebar = () => {
        const newState = !isSidebarCollapsed;
        setIsSidebarCollapsed(newState);
        localStorage.setItem('sidebarCollapsed', String(newState));
    };

    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen((prev) => !prev);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    return (
        <div
            className="min-h-screen bg-gray-50 text-gray-700 transition-colors dark:bg-dark dark:text-white"
            // Live sidebar width, consumed by fixed-position bars (e.g. the
            // Attribution staged-changes footer) to offset their left edge at
            // lg+ via `lg:left-(--sidebar-w,0px)`. Below lg the sidebar
            // is hidden, so consumers fall back to a 0 offset.
            style={{ '--sidebar-w': isSidebarCollapsed ? '4rem' : '16rem' } as CSSProperties}
        >
            <div className="flex">
                {/* Desktop Sidebar */}
                <Sidebar
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={toggleSidebar}
                />

                {/* Main content area */}
                <div className="flex flex-1 flex-col min-w-0 overflow-x-hidden">
                    <Header
                        isMobileMenuOpen={isMobileMenuOpen}
                        onToggleMobileMenu={toggleMobileMenu}
                    />

                    {/* Mobile navigation drawer */}
                    <MobileDrawer open={isMobileMenuOpen} onClose={closeMobileMenu}>
                        <div className="flex h-full flex-col overflow-y-auto">
                            {/* Header with logo */}
                            <div className="flex h-[57px] shrink-0 items-center border-b border-gray-200 px-4 dark:border-gray-700">
                                <Link to="/dashboard" onClick={closeMobileMenu}>
                                    <LogoFull className="h-8 w-auto" />
                                </Link>
                            </div>

                            {/* Main navigation */}
                            <nav aria-label="Main" className="flex-1 space-y-1 px-3 py-4">
                                {mainNavItems.map((item) => {
                                    const Icon = item.icon;
                                    const active = isActive(item.path);
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            onClick={closeMobileMenu}
                                            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-base transition-colors ${
                                                active
                                                    ? 'bg-gray-100 font-semibold text-accent dark:bg-secondary dark:text-primary-light'
                                                    : 'text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-secondary'
                                            }`}
                                        >
                                            <Icon className={`h-6 w-6 ${active ? 'text-accent dark:text-primary-light' : 'text-gray-500 dark:text-primary-light'}`} />
                                            {item.label}
                                        </Link>
                                    );
                                })}

                                {/* Separator */}
                                <div className="my-1 border-b border-gray-200 dark:border-gray-700" />

                                {/* Secondary navigation */}
                                {filteredBottomNavItems.map((item) => {
                                    const Icon = item.icon;
                                    const active = isActive(item.path);
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            onClick={closeMobileMenu}
                                            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-base transition-colors ${
                                                active
                                                    ? 'bg-gray-100 font-semibold text-accent dark:bg-secondary dark:text-primary-light'
                                                    : 'text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-secondary'
                                            }`}
                                        >
                                            <Icon className={`h-6 w-6 ${active ? 'text-accent dark:text-primary-light' : 'text-gray-500 dark:text-primary-light'}`} />
                                            {item.label}
                                        </Link>
                                    );
                                })}

                                </nav>

                            {/* Settings at bottom */}
                            <div className="shrink-0 space-y-1 border-t border-gray-200 px-3 py-4 dark:border-gray-700">
                                {/* Theme toggle */}
                                <button
                                    onClick={toggleTheme}
                                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-base text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-secondary"
                                >
                                    {effectiveTheme === 'dark' ? (
                                        <SunIcon className="h-6 w-6 text-gray-500 dark:text-primary-light" />
                                    ) : (
                                        <MoonIcon className="h-6 w-6 text-gray-500 dark:text-primary-light" />
                                    )}
                                    {effectiveTheme === 'dark' ? 'Light mode' : 'Dark mode'}
                                </button>

                                {/* Customize link */}
                                <Link
                                    to="/settings"
                                    onClick={closeMobileMenu}
                                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-base transition-colors ${
                                        isActive('/settings')
                                            ? 'bg-gray-100 font-semibold text-accent dark:bg-secondary dark:text-primary-light'
                                            : 'text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-secondary'
                                    }`}
                                >
                                    <AdjustmentsHorizontalIcon className={`h-6 w-6 ${isActive('/settings') ? 'text-accent dark:text-primary-light' : 'text-gray-500 dark:text-primary-light'}`} />
                                    Customize
                                </Link>
                            </div>

                            {/* User menu at bottom */}
                            <div className="shrink-0 border-t border-gray-300 px-4 py-3 dark:border-gray-700">
                                <Menu as="div" className="relative">
                                    <Menu.Button className="flex w-full items-center gap-3 rounded-md p-2 transition-colors hover:bg-gray-100 dark:hover:bg-secondary">
                                        <UserCircleIcon className="h-6 w-6 shrink-0 text-gray-500 dark:text-primary-light" />
                                        <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-gray-900 dark:text-white">
                                            {user?.email?.split('@')[0] || 'User'}
                                        </span>
                                    </Menu.Button>
                                    <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                    >
                                        <Menu.Items className="absolute bottom-full left-0 z-1000 mb-2 w-48 origin-bottom-left rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-hidden dark:border-gray-600 dark:bg-secondary">
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <Link
                                                        to="/profile"
                                                        onClick={closeMobileMenu}
                                                        className={`${
                                                            active ? 'bg-gray-100 dark:bg-accent' : ''
                                                        } flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-white`}
                                                    >
                                                        <UserIcon className="h-4 w-4" />
                                                        Account
                                                    </Link>
                                                )}
                                            </Menu.Item>
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <Link
                                                        to="/data"
                                                        onClick={closeMobileMenu}
                                                        className={`${
                                                            active ? 'bg-gray-100 dark:bg-accent' : ''
                                                        } flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-white`}
                                                    >
                                                        <ArrowsUpDownIcon className="h-4 w-4" />
                                                        Data
                                                    </Link>
                                                )}
                                            </Menu.Item>
                                            {/* Separator */}
                                            <div className="my-1 border-t border-gray-200 dark:border-gray-600" />
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <button
                                                        onClick={() => { logout(); closeMobileMenu(); }}
                                                        className={`${
                                                            active ? 'bg-gray-100 dark:bg-accent' : ''
                                                        } flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-white`}
                                                    >
                                                        <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                                                        Sign out
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        </Menu.Items>
                                    </Transition>
                                </Menu>
                            </div>
                        </div>
                    </MobileDrawer>

                    <InstallPromptBanner />

                    {/* Page content */}
                    <main className="flex-1 px-4 pb-4 pt-2 md:px-6 md:pb-6 md:pt-2 lg:px-8 lg:pb-8 lg:pt-3">
                        <div className={isFullWidth ? '' : 'max-w-(--breakpoint-2xl)'}>
                            {children}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
